import { unwrapResponse } from './apiClient';
import { authService } from './authService';
import type { HourCycle } from '../shared/dateTimePreferences';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
const ADMIN_API_URL = `${API_BASE_URL}/api/platform`;

/**
 * Fetch JSON and unwrap IActionResult serialization wrapper.
 */
async function fetchJson<T>(response: Response): Promise<T> {
  const raw = await response.json();
  return unwrapResponse<T>(raw);
}

interface AuthToken {
  token: string;
  refreshToken: string;
}

/**
 * Token helpers — check both sessionStorage (used by authService / OAuth flow)
 * and localStorage (used by magic-link / legacy flow) so that tokens written by
 * either path are available to adminApi callers.
 */
function getAuthToken(): string {
  // Prefer the OAuth-flow token stored by authService in sessionStorage
  try {
    const authState = sessionStorage.getItem('auth_state');
    if (authState) {
      const parsed = JSON.parse(authState);
      if (parsed.token) return parsed.token;
    }
  } catch { /* ignore */ }
  return localStorage.getItem('authToken') || '';
}

function getRefreshToken(): string {
  try {
    const authState = sessionStorage.getItem('auth_state');
    if (authState) {
      const parsed = JSON.parse(authState);
      if (parsed.refreshToken) return parsed.refreshToken;
    }
  } catch { /* ignore */ }
  return localStorage.getItem('refreshToken') || '';
}

function setTokens(auth: AuthToken): void {
  localStorage.setItem('authToken', auth.token);
  localStorage.setItem('refreshToken', auth.refreshToken);
  // Also update sessionStorage so authService stays in sync
  try {
    const existing = sessionStorage.getItem('auth_state');
    const state = existing ? JSON.parse(existing) : {};
    state.token = auth.token;
    state.refreshToken = auth.refreshToken;
    sessionStorage.setItem('auth_state', JSON.stringify(state));
  } catch { /* ignore */ }
}

function clearTokens(): void {
  authService.resetSession();
  localStorage.removeItem('authToken');
  localStorage.removeItem('refreshToken');
  sessionStorage.removeItem('auth_state');
}

const adminApi = {
  // Auth
  async login(email: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/auth/magic-link`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, returnUrl: window.location.origin }),
    });
    
    if (!response.ok) {
      throw new Error('Login failed');
    }
  },

  async verifyMagicLink(token: string): Promise<AuthToken & { user: any }> {
    const response = await fetch(`${API_BASE_URL}/api/auth/magic-link/verify?token=${encodeURIComponent(token)}`);
    
    if (!response.ok) {
      throw new Error('Invalid or expired token');
    }
    
    const data = await response.json();
    const unwrapped = unwrapResponse<any>(data);
    if (unwrapped.token) {
      setTokens({ token: unwrapped.token, refreshToken: unwrapped.refreshToken });
    }
    return unwrapped;
  },

  async logout(): Promise<void> {
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      await fetch(`${API_BASE_URL}/api/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      }).catch(() => {});
    }
    clearTokens();
  },

  async generateMobileEnrollmentQr(request: MobileEnrollmentQrRequest): Promise<MobileEnrollmentQrResponse> {
    const response = await fetch(`${API_BASE_URL}/api/mobile/enroll/qr`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error('Failed to generate mobile enrollment payload');
    }

    return fetchJson<MobileEnrollmentQrResponse>(response);
  },

  async getCurrentUser(): Promise<any> {
    const sessionRevision = authService.getSessionRevision();
    const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });
    
    if (!response.ok) {
      throw new Error('Not authenticated');
    }
    
    const data = unwrapResponse<any>(await response.json());
    if (data.user) {
      data.user.id = data.user.id || data.user.userId;
      authService.adoptUser(data.user, sessionRevision);
    }
    return data.user;
  },

  async getTenantInfo(): Promise<{ tenantId: string; slug: string; name: string }> {
    const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch tenant info');
    }
    
    const data = unwrapResponse<any>(await response.json());
    return {
      tenantId: data.user?.tenantId || '',
      slug: data.user?.tenantSlug || '',
      name: data.user?.tenantName || ''
    };
  },

  // Tenants
  async getTenants(params?: { skip?: number; take?: number }): Promise<{ tenants: Tenant[]; total: number }> {
    const searchParams = new URLSearchParams();
    if (params?.skip) searchParams.append('skip', params.skip.toString());
    if (params?.take) searchParams.append('take', (params.take || 50).toString());

    const response = await fetch(`${ADMIN_API_URL}/tenants?${searchParams}`, {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch tenants');
    }
    
    const data = await fetchJson<any>(response);
    return {
      tenants: Array.isArray(data?.tenants) ? data.tenants : [],
      total: typeof data?.total === 'number' ? data.total : 0,
    };
  },

  async getTenant(tenantId: string): Promise<TenantDetail> {
    const response = await fetch(`${ADMIN_API_URL}/tenants/${tenantId}`, {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch tenant');
    }
    
    return fetchJson(response);
  },

  async getTenantRoutingDiagnostics(alias: string): Promise<TenantRoutingDiagnostics> {
    const trimmed = (alias || '').trim();
    if (!trimmed) {
      throw new Error('Alias is required');
    }

    const response = await fetch(`${API_BASE_URL}/api/tenant-directory/diagnostics?alias=${encodeURIComponent(trimmed)}`, {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch tenant routing diagnostics');
    }

    return fetchJson(response);
  },

  async getPlatformSmsGateways(): Promise<SmsGateway[]> {
    const response = await fetch(`${API_BASE_URL}/api/platform/sms-gateways`, {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch SMS gateways');
    }

    const data = await fetchJson<{ gateways: SmsGateway[] }>(response);
    return Array.isArray(data?.gateways) ? data.gateways : [];
  },

  async createPlatformSmsGateway(data: CreateSmsGatewayRequest): Promise<SmsGateway> {
    const response = await fetch(`${API_BASE_URL}/api/platform/sms-gateways`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(text || 'Failed to create SMS gateway');
    }

    return fetchJson<SmsGateway>(response);
  },

  async updatePlatformSmsGateway(gatewayId: string, data: UpdateSmsGatewayRequest): Promise<SmsGateway> {
    const response = await fetch(`${API_BASE_URL}/api/platform/sms-gateways/${gatewayId}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(text || 'Failed to update SMS gateway');
    }

    return fetchJson<SmsGateway>(response);
  },

  async deletePlatformSmsGateway(gatewayId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/platform/sms-gateways/${gatewayId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });

    if (!response.ok) {
      throw new Error('Failed to delete SMS gateway');
    }
  },

  async triggerPlatformSmsGatewayCollect(gatewayId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/platform/sms-gateways/${gatewayId}/trigger-collect`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(text || 'Failed to trigger SMS collect');
    }
  },

  async generatePlatformSmsGatewayOnboardingPayload(
    gatewayId: string,
    request: SmsGatewayOnboardingPayloadRequest,
  ): Promise<SmsGatewayOnboardingPayloadResponse> {
    const response = await fetch(`${API_BASE_URL}/api/platform/sms-gateways/${gatewayId}/onboarding-payload`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error('Failed to generate SMS gateway onboarding payload');
    }

    return fetchJson<SmsGatewayOnboardingPayloadResponse>(response);
  },

  async getPlatformSmsDispatchMessages(params?: { take?: number; skip?: number; status?: string; gatewayId?: string }): Promise<SmsDispatchMessage[]> {
    const query = new URLSearchParams();

    if (params?.take != null) query.set('take', String(params.take));
    if (params?.skip != null) query.set('skip', String(params.skip));
    if (params?.status) query.set('status', params.status);
    if (params?.gatewayId) query.set('gatewayId', params.gatewayId);

    const response = await fetch(`${API_BASE_URL}/api/platform/sms-dispatch/messages?${query.toString()}`, {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch SMS dispatch messages');
    }

    const data = await fetchJson<{ messages: SmsDispatchMessage[] }>(response);
    return Array.isArray(data?.messages) ? data.messages : [];
  },

  async allocatePlatformSmsDispatchMessages(take = 100): Promise<{ requested: number; allocated: number }> {
    const response = await fetch(`${API_BASE_URL}/api/platform/sms-dispatch/allocate?take=${encodeURIComponent(String(take))}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });

    if (!response.ok) {
      throw new Error('Failed to allocate SMS dispatch messages');
    }

    return fetchJson<{ requested: number; allocated: number }>(response);
  },

  async dequeuePlatformSmsDispatchMessage(dispatchMessageId: string): Promise<SmsDispatchMessage> {
    const response = await fetch(`${API_BASE_URL}/api/platform/sms-dispatch/messages/${dispatchMessageId}/dequeue`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });

    if (!response.ok) {
      throw new Error('Failed to dequeue SMS dispatch message');
    }

    return fetchJson<SmsDispatchMessage>(response);
  },

  async enqueuePlatformSmsDispatchMessage(data: CreatePlatformSmsDispatchMessageRequest): Promise<SmsDispatchMessage> {
    const response = await fetch(`${API_BASE_URL}/api/platform/sms-dispatch/messages`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(text || 'Failed to enqueue SMS dispatch message');
    }

    return fetchJson<SmsDispatchMessage>(response);
  },

  async createTenant(data: CreateTenantRequest): Promise<TenantDetail> {
    const response = await fetch(`${ADMIN_API_URL}/tenants`, {
      method: 'POST',
      headers: { 
        Authorization: `Bearer ${getAuthToken()}`,
        'Content-Type': 'application/json' 
      },
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      const text = await response.text();
      let errorMsg = 'Failed to create tenant';
      try {
        const parsed = JSON.parse(text);
        const unwrapped = unwrapResponse<any>(parsed);
        errorMsg = unwrapped.error || errorMsg;
      } catch { /* use default */ }
      throw new Error(errorMsg);
    }
    
    return fetchJson(response);
  },

  async updateTenant(
    tenantId: string,
    data: Partial<CreateTenantRequest> & {
      subscriptionState?: string;
      evaluationDays?: number;
      subscriptionBillingPeriod?: 'Monthly' | 'Annual' | string;
      subscriptionLastPaymentAt?: string;
    },
  ): Promise<TenantDetail> {
    const response = await fetch(`${ADMIN_API_URL}/tenants/${tenantId}`, {
      method: 'PUT',
      headers: { 
        Authorization: `Bearer ${getAuthToken()}`,
        'Content-Type': 'application/json' 
      },
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      throw new Error('Failed to update tenant');
    }
    
    return fetchJson(response);
  },

  async getSubscriptionPlans(includeInactive: boolean = false): Promise<SubscriptionPlan[]> {
    const response = await fetch(`${ADMIN_API_URL}/plans?includeInactive=${includeInactive ? 'true' : 'false'}`, {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch subscription plans');
    }

    return fetchJson(response);
  },

  async createSubscriptionPlan(data: SubscriptionPlanUpsertRequest): Promise<SubscriptionPlan> {
    const response = await fetch(`${ADMIN_API_URL}/plans`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Failed to create subscription plan');
    }

    return fetchJson(response);
  },

  async updateSubscriptionPlan(planId: string, data: SubscriptionPlanUpsertRequest): Promise<SubscriptionPlan> {
    const response = await fetch(`${ADMIN_API_URL}/plans/${planId}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Failed to update subscription plan');
    }

    return fetchJson(response);
  },

  async deleteSubscriptionPlan(planId: string): Promise<void> {
    const response = await fetch(`${ADMIN_API_URL}/plans/${planId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });

    if (!response.ok) {
      throw new Error('Failed to delete subscription plan');
    }
  },

  async getEvaluationCardConfig(): Promise<EvaluationCardConfig> {
    const response = await fetch(`${ADMIN_API_URL}/plans/config/evaluation`, {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch evaluation card configuration');
    }

    return fetchJson(response);
  },

  async updateEvaluationCardConfig(data: EvaluationCardConfig): Promise<EvaluationCardConfig> {
    const response = await fetch(`${ADMIN_API_URL}/plans/config/evaluation`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      let errorMsg = 'Failed to update evaluation card configuration';
      if (text) {
        try {
          const parsed = JSON.parse(text);
          const unwrapped = unwrapResponse<any>(parsed);
          errorMsg = unwrapped?.error || unwrapped?.message || errorMsg;
        } catch {
          errorMsg = text;
        }
      }
      throw new Error(errorMsg);
    }

    return fetchJson(response);
  },

  async suspendTenant(tenantId: string): Promise<void> {
    const response = await fetch(`${ADMIN_API_URL}/tenants/${tenantId}/suspend`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });
    
    if (!response.ok) {
      throw new Error('Failed to suspend tenant');
    }
  },

  async reactivateTenant(tenantId: string): Promise<void> {
    const response = await fetch(`${ADMIN_API_URL}/tenants/${tenantId}/reactivate`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });
    
    if (!response.ok) {
      throw new Error('Failed to reactivate tenant');
    }
  },

  async deleteTenant(tenantId: string): Promise<void> {
    const response = await fetch(`${ADMIN_API_URL}/tenants/${tenantId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });
    
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      const detail = data?.value?.error || data?.error || 'Failed to delete tenant';
      throw new Error(detail);
    }

    // Azure Functions isolated worker wraps IActionResult as HTTP 200 even for
    // 404/400 errors.  Read and unwrap the body so the inner status is checked.
    const raw = await response.json().catch(() => null);
    if (raw) {
      unwrapResponse(raw); // throws if inner statusCode >= 400
    }
  },

  async regenerateApiKey(tenantId: string, keyId: string): Promise<{ key: string }> {
    const response = await fetch(`${ADMIN_API_URL}/tenants/${tenantId}/apikeys/${keyId}/regenerate`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });
    
    if (!response.ok) {
      throw new Error('Failed to regenerate API key');
    }
    
    return fetchJson(response);
  },

  async getTenantUsage(tenantId: string, days: number = 30): Promise<TenantUsage> {
    const response = await fetch(`${ADMIN_API_URL}/tenants/${tenantId}/usage?days=${days}`, {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch usage');
    }
    
    return fetchJson(response);
  },

  // Audit
  async getAuditLog(params?: { tenantId?: string; action?: string; fromDate?: string; toDate?: string; limit?: number }): Promise<{ entries: AuditEntry[] }> {
    const searchParams = new URLSearchParams();
    if (params?.tenantId) searchParams.append('tenantId', params.tenantId);
    if (params?.action) searchParams.append('action', params.action);
    if (params?.fromDate) searchParams.append('fromDate', params.fromDate);
    if (params?.toDate) searchParams.append('toDate', params.toDate);
    if (params?.limit) searchParams.append('limit', params.limit.toString());

    const response = await fetch(`${ADMIN_API_URL}/audit?${searchParams}`, {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch audit log');
    }
    
    const data = await fetchJson<any>(response);
    return {
      entries: Array.isArray(data?.entries) ? data.entries : [],
    };
  },

  // Platform-level user management (cross-tenant)
  async getTenantUsers(tenantId: string, params?: { skip?: number; take?: number; search?: string }): Promise<{ users: PlatformUser[]; total: number }> {
    const searchParams = new URLSearchParams();
    if (params?.skip) searchParams.append('skip', params.skip.toString());
    if (params?.take) searchParams.append('take', params.take.toString());
    if (params?.search) searchParams.append('search', params.search);

    const response = await fetch(`${ADMIN_API_URL}/tenants/${tenantId}/users?${searchParams}`, {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch tenant users');
    }

    const data = await fetchJson<any>(response);
    return {
      users: Array.isArray(data?.users) ? data.users : [],
      total: typeof data?.total === 'number' ? data.total : 0,
    };
  },

  async inviteTenantUser(tenantId: string, request: PlatformInviteRequest): Promise<PlatformUser> {
    const response = await fetch(`${ADMIN_API_URL}/tenants/${tenantId}/users/invite`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getAuthToken()}`,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Failed to invite user' }));
      throw new Error(err.error || 'Failed to invite user');
    }

    return fetchJson(response);
  },

  async updateTenantUser(tenantId: string, userId: string, request: PlatformUpdateUserReq): Promise<PlatformUser> {
    const response = await fetch(`${ADMIN_API_URL}/tenants/${tenantId}/users/${userId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getAuthToken()}`,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Failed to update user' }));
      throw new Error(err.error || 'Failed to update user');
    }

    return fetchJson(response);
  },

  async deleteTenantUser(tenantId: string, userId: string): Promise<void> {
    const response = await fetch(`${ADMIN_API_URL}/tenants/${tenantId}/users/${userId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Failed to delete user' }));
      throw new Error(err.error || 'Failed to delete user');
    }

    // Azure Functions isolated worker wraps IActionResult as HTTP 200 even for
    // 404/400 errors.  Read and unwrap the body so the inner status is checked.
    const raw = await response.json().catch(() => null);
    if (raw) {
      unwrapResponse(raw); // throws if inner statusCode >= 400
    }
  },
};

// NOTE: The tenantApi object has been moved to tenantApi.ts as part of the
// Portal Unification (PU) refactor. Import from '../services/tenantApi' instead.

export interface ApiKeyInfo {
  keyId: string;
  keyPrefix: string;
  reference: string | null;
  createdAt: string;
  isActive: boolean;
  deactivatedAt: string | null;
  purgeAt: string | null;
}

export interface CreateOverrideRequest {
  userId?: string;
  groupId?: string;
  startTime: string;
  endTime: string;
  reason?: string;
}

export interface Rule {
  ruleId: string;
  tenantId: string;
  name: string;
  description?: string;
  triggerType: string;
  triggerFilters?: string;
  conditionsJson?: string;
  actionsJson?: string;
  actions?: RuleAction[];
  priority: number;
  isEnabled: boolean;
  continueProcessing: boolean;
  configJson?: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ConditionGroup {
  logic: 'AND' | 'OR';
  conditions: Condition[];
  groups: ConditionGroup[];
}

export interface Condition {
  field: string;
  operator: string;
  value: any;
}

export interface RuleAction {
  type: string;
  config: ActionConfig;
}

export interface ActionConfig {
  url?: string;
  method?: string;
  headers?: Record<string, string>;
  body?: any;
  queryParams?: Record<string, string>;
  channels?: string[];
  template?: string;
  priorityMapping?: Record<string, number>;
}

export interface CreateRuleRequest {
  name: string;
  description?: string;
  triggerType: string;
  triggerFilters?: any;
  conditions?: ConditionGroup;
  actions: RuleAction[];
  priority?: number;
  isEnabled?: boolean;
  continueProcessing?: boolean;
  configJson?: string;
}

// Types
export interface Tenant {
  tenantId: string;
  name: string;
  slug: string;
  region: string;
  status: string;
  plan: string;
  subscriptionState?: string;
  evaluationExpiresAt?: string | null;
  planExpiry?: string | null;
  planDaysLeft?: number | null;
  subscriptionBillingPeriod?: string;
  subscriptionLastPaymentAt?: string | null;
  clientInitialSignupAt?: string | null;
  userCount: number;
  createdAt: string;
}

export interface TenantDetail extends Tenant {
  retentionDays: number;
  authType: string;
  smsGatewayAlertMode?: 'Disabled' | 'TenantAdminDashboard' | 'TenantAndSuperAdminDashboard' | string;
  usage?: TenantUsage;
  apiKey?: string;
  adminUsers: { userId: string; email: string; displayName: string; status: string }[];
}

export interface TenantRoutingDiagnosticsCounts {
  customDomain: number;
  subdomain: number;
  slug: number;
}

export interface TenantRoutingDiagnosticMatch {
  source: 'CustomDomain' | 'Subdomain' | 'Slug' | string;
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  region: string;
}

export interface TenantRoutingResolution {
  resolved: boolean;
  ambiguous: boolean;
  reason?: string | null;
  matchedAlias?: string | null;
  aliasType?: string | null;
  tenantId?: string | null;
  tenantName?: string | null;
  tenantSlug?: string | null;
  region?: string | null;
  routingVersion: string;
  deploymentId?: string | null;
}

export interface TenantRoutingDiagnostics {
  aliasInput: string;
  aliasNormalized: string;
  counts: TenantRoutingDiagnosticsCounts;
  matches: TenantRoutingDiagnosticMatch[];
  resolution: TenantRoutingResolution;
  routingVersion: string;
  deploymentId?: string | null;
  error?: string | null;
}

export interface TenantUsage {
  alertsPerDay: number;
  smsCount: number;
  emailCount: number;
  totalAlerts: number;
  integrations: number;
  activeSchedules: number;
}

export type SmsGatewayScopeType = 'Platform' | 'TenantDedicated';

export interface SmsGateway {
  gatewayId: string;
  name: string;
  scopeType: SmsGatewayScopeType;
  tenantId?: string | null;
  region: string;
  deviceRegistrationId?: string | null;
  connectionId?: string | null;
  isEnabled: boolean;
  status: 'Online' | 'Offline' | 'Inactive' | string;
  lastSeenAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSmsGatewayRequest {
  name: string;
  scopeType: SmsGatewayScopeType;
  tenantId?: string | null;
  region: string;
  deviceRegistrationId?: string;
  isEnabled?: boolean;
}

export interface UpdateSmsGatewayRequest {
  name?: string;
  scopeType?: SmsGatewayScopeType;
  tenantId?: string | null;
  region?: string;
  deviceRegistrationId?: string;
  isEnabled?: boolean;
  status?: string;
}

export interface SmsDispatchMessage {
  dispatchMessageId: string;
  tenantId: string;
  recipientPhone: string;
  messageText: string;
  priority: number;
  region: string;
  status: string;
  attemptCount: number;
  maxRetries: number;
  retryAfterUtc?: string | null;
  correlationId?: string | null;
  allocationReason?: string | null;
  allocatedGatewayId?: string | null;
  leaseId?: string | null;
  leaseExpiresUtc?: string | null;
  lastError?: string | null;
  createdAt: string;
  updatedAt: string;
  collectedAt?: string | null;
  finalizedAt?: string | null;
}

export interface CreateSmsDispatchMessageRequest {
  recipientPhone: string;
  messageText: string;
  priority?: number;
  maxRetries?: number;
  correlationId?: string;
  region?: string;
  preferredGatewayId?: string;
}

export interface CreatePlatformSmsDispatchMessageRequest extends CreateSmsDispatchMessageRequest {
  tenantId: string;
}

export interface SubscriptionPlanFeature {
  featureId: string;
  text: string;
  icon?: string;
  sortOrder: number;
  isActive: boolean;
}

export interface SubscriptionPlan {
  planId: string;
  levelLabel: string;
  planName: string;
  description?: string;
  monthlyPrice?: string;
  annualPrice?: string;
  ctaLabel: string;
  usersDisplay?: string;
  supportDisplay?: string;
  sortOrder: number;
  isFeatured: boolean;
  isActive: boolean;
  features: SubscriptionPlanFeature[];
  createdAt: string;
  updatedAt: string;
}

export interface EvaluationCardConfig {
  enabled: boolean;
  defaultDays: number;
}

export interface SubscriptionPlanFeatureUpsertRequest {
  featureId?: string;
  text: string;
  icon?: string;
  sortOrder?: number;
}

export interface SubscriptionPlanUpsertRequest {
  levelLabel: string;
  planName: string;
  description?: string;
  monthlyPrice?: string;
  annualPrice?: string;
  ctaLabel: string;
  usersDisplay?: string;
  supportDisplay?: string;
  sortOrder: number;
  isFeatured?: boolean;
  features: SubscriptionPlanFeatureUpsertRequest[];
}

export interface CreateTenantRequest {
  name: string;
  slug?: string;
  region?: string;
  plan?: string;
  retentionDays?: number;
  authType?: string;
  adminEmail: string;
  adminName?: string;
}

export interface User {
  id: string; // Add id for compatibility with ProfileSettings
  userId: string;
  email: string;
  displayName: string;
  phone?: string;
  status: string;
  timezone?: string;
  dateTimeLocale?: string | null;
  hourCycle?: HourCycle | null;
  roles: string[];
  permissions?: string[];
  createdAt: string;
}

export interface UserDetail extends User {
  avatarUrl?: string;
  groups: { groupId: string; name: string; type: string }[];
  updatedAt?: string;
}

export interface InviteUserRequest {
  email: string;
  displayName?: string;
  phone?: string;
  timezone?: string;
  dateTimeLocale?: string | null;
  hourCycle?: HourCycle | null;
  roles?: string[];
  groupIds?: string[];
}

export interface UpdateUserRequest {
  email?: string;
  displayName?: string;
  phone?: string;
  timezone?: string;
  dateTimeLocale?: string | null;
  hourCycle?: HourCycle | null;
  status?: string;
}

export interface Group {
  groupId: string;
  name: string;
  type: string;
  memberCount?: number;
}

export interface Role {
  roleId: string;
  name: string;
  description?: string;
  memberCount?: number;
}

export interface Permission {
  permissionId: number;
  name: string;
  description?: string;
}

export interface Integration {
  integrationId: string;
  type: string;
  name: string;
  configJson?: string;
  isEnabled: boolean;
  keyCount?: number;
  createdAt: string;
}

export interface CreateIntegrationRequest {
  type: string;
  name: string;
  configJson?: string;
}

// ── Inbound Integrations (receives alerts from external systems) ─────────

export interface InboundIntegration {
  inboundIntegrationId: string;
  name: string;
  provider: string;
  nonAlert?: boolean;
  transformationType: string;
  transformationConfig?: string;
  isEnabled: boolean;
  keyCount?: number;
  receiptCount?: number;
  schemaVersionCount?: number;
  workflowReferenceCount?: number;
  deletedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateInboundIntegrationRequest {
  name: string;
  payloadMode?: 'Direct' | 'Normalise';
  provider?: string;
  nonAlert?: boolean;
  transformationType?: string;
  transformationConfig?: string;
}

export interface UpdateInboundIntegrationRequest {
  name?: string;
  provider?: string;
  nonAlert?: boolean;
  transformationType?: string;
  transformationConfig?: string;
  isEnabled?: boolean;
}

// ── Outbound Integrations (pushes data to external systems) ──────────────

export interface OutboundIntegration {
  outboundIntegrationId: string;
  name: string;
  provider: string;
  baseUrl?: string;
  authType: string;
  authSecretRef?: string;
  configJson?: string;
  isEnabled: boolean;
  retainedReferenceCount?: number;
  deletedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateOutboundIntegrationRequest {
  name: string;
  provider: string;
  baseUrl?: string;
  authType?: string;
  authSecretRef?: string;
  configJson?: string;
}

export interface UpdateOutboundIntegrationRequest {
  name?: string;
  provider?: string;
  baseUrl?: string;
  authType?: string;
  authSecretRef?: string;
  configJson?: string;
  isEnabled?: boolean;
}

export interface OutboundIntegrationAction {
  outboundIntegrationActionId: string;
  outboundIntegrationId: string;
  name: string;
  actionKey: string;
  method: string;
  pathTemplate?: string;
  bodyTemplate?: string;
  isDeliveryCapable?: boolean;
  deliveryInputs?: Array<{ name: string; path: string; required: boolean }>;
  deliveryProfile?: {
    profile: string;
    description: string;
    requiredFields: string[];
    supportsAttachments: boolean;
    maxAttachmentCount?: number | null;
    maxBytesPerAttachment?: number | null;
  } | null;
  requestSchemaJson?: string;
  requestMappingsJson?: string;
  responseSchemaJson?: string;
  responseMappingsJson?: string;
  headers?: Record<string, string>;
  ticketIdVariable?: string;
  isEnabled: boolean;
  isSystemPreset: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateOutboundIntegrationActionRequest {
  name: string;
  actionKey: string;
  method: string;
  pathTemplate?: string;
  bodyTemplate?: string;
  isDeliveryCapable?: boolean;
  deliveryInputs?: Array<{ name: string; path: string; required: boolean }>;
  requestSchemaJson?: string;
  requestMappingsJson?: string;
  responseSchemaJson?: string;
  responseMappingsJson?: string;
  headers?: Record<string, string>;
  ticketIdVariable?: string;
  isEnabled?: boolean;
  sortOrder?: number;
}

export interface UpdateOutboundIntegrationActionRequest {
  name?: string;
  actionKey?: string;
  method?: string;
  pathTemplate?: string;
  bodyTemplate?: string;
  isDeliveryCapable?: boolean;
  deliveryInputs?: Array<{ name: string; path: string; required: boolean }>;
  requestSchemaJson?: string;
  requestMappingsJson?: string;
  responseSchemaJson?: string;
  responseMappingsJson?: string;
  headers?: Record<string, string>;
  ticketIdVariable?: string;
  isEnabled?: boolean;
  sortOrder?: number;
}

export interface RoutingConditions {
  severity?: string[];
  service?: string[];
  source?: string[];
}

export interface NotificationTarget {
  type: 'Channel' | 'User' | 'Group' | 'OnCall' | 'EscalationPolicy' | 'Integration';
  id: string;
}

export interface RoutingActions {
  targets?: NotificationTarget[];
  notifyGroup?: string;
  notifyUserId?: string;
  channels?: string[];
  escalationPolicy?: string;
  createTicket?: boolean;
  ticketIntegrationId?: string;
  webhookUrls?: string[];
}

export interface NotificationChannel {
  channelId: string;
  tenantId: string;
  emailSenderIdentityId?: string | null;
  name: string;
  channelType: 'Teams' | 'TeamsWorkflow' | 'Email' | 'SMS' | 'Webhook';
  configJson: string;
  isEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateNotificationChannelRequest {
  name: string;
  channelType: string;
  configJson: string;
  emailSenderIdentityId?: string | null;
}

export interface UpdateNotificationChannelRequest {
  name?: string;
  channelType?: string;
  configJson?: string;
  emailSenderIdentityId?: string | null;
}

export interface UserNotificationPreference {
  userId: string;
  tenantId: string;
  method: string;
  priority: number;
  isEnabled: boolean;
  configJson: string;
}

export interface RoutingRule {
  ruleId: string;
  name: string;
  priority: number;
  conditions: RoutingConditions;
  actions: RoutingActions;
  isEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateRoutingRuleRequest {
  name: string;
  priority: number;
  conditions: RoutingConditions;
  actions: RoutingActions;
}

export interface UpdateRoutingRuleRequest {
  name?: string;
  priority?: number;
  conditions?: RoutingConditions;
  actions?: RoutingActions;
  isEnabled?: boolean;
}

export interface Schedule {
  scheduleId: string;
  name: string;
  timezone: string;
  rotationType: string;
  version?: 'current';
  scheduleType?: 'Primary' | 'Escalation';
  primaryWindowId?: string;
  linkedPrimaryScheduleId?: string;
  startDate: string;
  endDate?: string;
  isActive: boolean;
  members: ScheduleMember[];
  overrides?: ScheduleOverride[];
}

export interface ScheduleMember {
  userId: string;
  userName: string;
  rotationOrder: number;
}

export interface ScheduleOverride {
  overrideId: string;
  userId?: string;
  groupId?: string;
  startTime: string;
  endTime: string;
  reason?: string;
}

export interface CreateScheduleRequest {
  name: string;
  timezone: string;
  rotationType: string;
  version?: 'current';
  scheduleType?: 'Primary' | 'Escalation';
  primaryWindowId?: string | null;
  linkedPrimaryScheduleId?: string | null;
  isActive?: boolean;
  startDate: string;
  endDate?: string;
  memberIds?: string[];
}

export interface AuditEntry {
  id: string;
  tenantId?: string;
  userId?: string;
  action: string;
  entityType?: string;
  entityId?: string;
  details?: string;
  ipAddress?: string;
  timestamp: string;
  userEmail?: string;
  userDisplayName?: string;
}

export interface PlatformUser {
  userId: string;
  email: string;
  displayName: string;
  phone?: string;
  status: string;
  timezone?: string;
  roles: string[];
  createdAt: string;
}

export interface PlatformInviteRequest {
  email: string;
  displayName?: string;
  phone?: string;
  timezone?: string;
  role?: string;
}

export interface PlatformUpdateUserReq {
  displayName?: string;
  phone?: string;
  timezone?: string;
  status?: string;
  role?: string;
}

export interface MobileEnrollmentQrRequest {
  apiBaseUrl: string;
  tenantSlug?: string;
  tenantName?: string;
  env?: string;
  expiresInSeconds?: number;
}

export interface MobileEnrollmentQrResponse {
  payloadJson: string;
  expiresAtUnix: number;
}

export interface SmsGatewayOnboardingPayloadRequest {
  apiBaseUrl: string;
  signalRNegotiatePath?: string;
  expiresInSeconds?: number;
}

export interface SmsGatewayOnboardingPayloadResponse {
  payloadJson: string;
  expiresAtUnix: number;
}

export { adminApi, getAuthToken, getRefreshToken, setTokens, clearTokens };
